import type { ReactNode, Ref } from 'react';
import { forwardRef } from 'react';
import type { PopupActions, PopupProps } from 'reactjs-popup/dist/types';
import { useTheme } from 'styled-components/macro';

import DragAndResizeModal from 'components/Modal/DragAndResizeModal';
import { useResponsive } from 'contexts/ResponsiveContext';
// eslint-disable-next-line import/order -- path group order differs by config
import { ModalSizeE } from 'declarations/enums/modal';
import type { ScrollLockIdE } from 'declarations/enums/scroll';

import { getContentStyles } from './Popup.styled';

type PopupPropsT = PopupProps & {
  children: ReactNode;
  popupId?: ScrollLockIdE;
  isOpen?: boolean;
  showCloseBtn?: boolean;
  ref?: Ref<PopupActions>;
  /** Title shown in the modal header bar */
  modalTitle?: string | ReactNode;
  /** Preset modal size (overrides width/height when set) */
  size?: ModalSizeE;
  /** Initial width override */
  width?: number;
  /** Initial height override */
  height?: number;
  /** When true, content grows so scrollbar reaches bottom (e.g. New Ticket with many categories) */
  contentGrowsWithContent?: boolean;
  /** When true, modal height grows with content (no resize handle) */
  dynamicHeight?: boolean;
  /** When provided, modal auto-resizes vertically when validation errors appear or are removed */
  validationErrorCount?: number;
};

/**
 * Modal popup built on DragAndResizeModal: draggable by the title bar and
 * resizable by the handle in the bottom-right corner. Supports scroll lock
 * via popupId. API is compatible with previous reactjs-popup usage.
 */
const Popup = forwardRef<PopupActions, PopupPropsT>(
  (
    {
      children,
      onClose,
      popupId,
      isOpen = false,
      showCloseBtn = false,
      contentStyle,
      modalTitle,
      size = ModalSizeE.MD,
      validationErrorCount,
      ...restProps
    },
    _ref
  ) => {
    const theme = useTheme();
    const { isMobile } = useResponsive();

    const handleClose = (): void => {
      if (onClose) {
        onClose();
      }
    };

    const mergedContentStyle = getContentStyles(theme, isMobile, contentStyle);

    return (
      <DragAndResizeModal
        open={isOpen}
        handleClose={handleClose}
        popupId={popupId}
        size={size}
        showCloseButton={showCloseBtn !== false}
        dialogContentStyles={mergedContentStyle as Record<string, unknown>}
        modalTitle={modalTitle}
        validationErrorCount={validationErrorCount}
        isMobile={isMobile}
        {...restProps}
      >
        {children}
      </DragAndResizeModal>
    );
  }
);

Popup.displayName = 'Popup';

export default Popup;
